// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import type { Resolver } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Ticket, Plus, ChevronDown, ChevronUp, Trash2 } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, DataTable, Skeleton, Input, Select, ConfirmDialog } from '@/components/ui'
import type { Column } from '@/components/ui/DataTable'
import {
  usePromotions,
  useCreatePromotion,
  useDeletePromotion,
  usePromotionRedemptions,
  type Promotion,
  type PromotionRedemption,
} from '@/hooks/usePromotions'
import { useNotificationStore } from '@/stores'
import { formatBaht } from '@/lib/money'
import { Pagination } from '@/components/ui'
import { useDateFormatter } from '@/hooks/useDateFormatter'

// ─── Schemas ──────────────────────────────────────────────────────────────────

const promotionSchema = z.object({
  kind: z.string().min(1),
  value_type: z.enum(['fixed', 'percent']),
  value: z.coerce.number().min(0),
  currency: z.string().min(1).default('THB'),
  funding: z.string().min(1),
  starts_at: z.string().optional(),
  ends_at: z.string().optional(),
  max_redemptions: z.coerce.number().optional().nullable(),
  per_customer_limit: z.coerce.number().optional().nullable(),
  status: z.string().default('active'),
  code: z.string().optional(),
})

type PromotionFormData = z.infer<typeof promotionSchema>

// ─── Sub-component: Create Promotion Form ─────────────────────────────────────

function PromotionCreateForm({ onSuccess, onCancel }: { onSuccess: () => void; onCancel: () => void }) {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const addToast = useNotificationStore((s) => s.addToast)
  const createPromotion = useCreatePromotion()

  const { register, handleSubmit, reset, formState: { errors, isSubmitting } } = useForm<PromotionFormData>({
    resolver: zodResolver(promotionSchema) as unknown as Resolver<PromotionFormData>,
    defaultValues: {
      kind: '',
      value_type: 'fixed',
      value: 0,
      currency: 'THB',
      funding: '',
      status: 'active',
    },
  })

  const valueTypeOptions = [
    { value: 'fixed', label: t('promotions.value_type.fixed') },
    { value: 'percent', label: t('promotions.value_type.percent') },
  ]

  const onSubmit = async (data: PromotionFormData) => {
    const { code, ...rest } = data
    const conditions = code ? { code } : {}
    await createPromotion.mutateAsync({ ...rest, conditions })
    addToast({ type: 'success', message: t('promotions.form.create_success') })
    reset()
    onSuccess()
  }

  return (
    <Card>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="min-w-0">
            <Input
              label={t('promotions.fields.kind')}
              {...register('kind')}
              error={errors.kind?.message}
            />
          </div>
          <Select
            label={t('promotions.fields.value_type')}
            options={valueTypeOptions}
            {...register('value_type')}
          />
          <div className="min-w-0">
            <Input
              label={t('promotions.fields.value')}
              type="number"
              min={0}
              step="0.01"
              {...register('value')}
              error={errors.value?.message}
            />
          </div>
          <div className="min-w-0">
            <Input
              label={t('promotions.fields.funding')}
              {...register('funding')}
              error={errors.funding?.message}
            />
          </div>
          <div className="min-w-0">
            <Input label={t('promotions.fields.starts_at')} type="date" {...register('starts_at')} />
          </div>
          <div className="min-w-0">
            <Input label={t('promotions.fields.ends_at')} type="date" {...register('ends_at')} />
          </div>
          <div className="min-w-0">
            <Input
              label={t('promotions.fields.max_redemptions')}
              type="number"
              min={0}
              {...register('max_redemptions')}
            />
          </div>
          <div className="min-w-0">
            <Input
              label={t('promotions.fields.per_customer_limit')}
              type="number"
              min={0}
              {...register('per_customer_limit')}
            />
          </div>
          <div className="sm:col-span-2 min-w-0">
            <Input
              label={t('promotions.fields.code')}
              {...register('code')}
            />
          </div>
        </div>
        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onCancel}>{tc('actions.cancel')}</Button>
          <Button type="submit" loading={isSubmitting}>{t('promotions.form.save_button')}</Button>
        </div>
      </form>
    </Card>
  )
}

// ─── Sub-component: Redemptions Detail ────────────────────────────────────────

function RedemptionsDetail({ promotionId }: { promotionId: number }) {
  const { t } = useTranslation('incentives')
  const { data, isLoading } = usePromotionRedemptions(promotionId)
  const redemptions: PromotionRedemption[] = data?.redemptions ?? []
  // data is PromotionDetail (extends Promotion with redemptions[]) from the hook
  const formatDate = useDateFormatter()

  const columns: Column<PromotionRedemption>[] = [
    { key: 'customer_id', header: t('promotions.redemptions.customer_id'), render: (r) => r.customer_id ?? '—' },
    { key: 'invoice_id', header: t('promotions.redemptions.invoice_id'), render: (r) => r.invoice_id ?? '—' },
    { key: 'applied_amount', header: t('promotions.redemptions.applied_amount'), render: (r) => formatBaht(r.applied_amount) },
    {
      key: 'status',
      header: t('promotions.redemptions.status'),
      render: (r) => <Badge variant="ghost">{t(`promotions.redemptions.status_values.${r.status}`)}</Badge>,
    },
    { key: 'redeemed_at', header: t('promotions.redemptions.redeemed_at'), render: (r) => (r.redeemed_at ? formatDate(r.redeemed_at) : '—'), hideBelow: 'md' },
  ]

  if (isLoading) return <Skeleton className="h-32" />

  if (!redemptions.length) {
    return (
      <EmptyState
        title={t('promotions.redemptions.empty_title')}
        description={t('promotions.redemptions.empty_description')}
      />
    )
  }

  return (
    <div>
      <h3 className="font-semibold text-sm text-base-content/70 uppercase tracking-wide mb-3">
        {t('promotions.redemptions.title')}
      </h3>
      <DataTable columns={columns} data={redemptions} keyExtractor={(r) => r.id} />
    </div>
  )
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export function PromotionsPage() {
  const { t } = useTranslation('incentives')
  const { t: tc } = useTranslation('common')
  const [page, setPage] = useState(1)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [formOpen, setFormOpen] = useState(false)

  const { data, isLoading, isFetching, error, refetch } = usePromotions(page)
  const promotions = data?.data ?? []

  const addToast = useNotificationStore((s) => s.addToast)
  const deletePromotion = useDeletePromotion()
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null)

  const handleDelete = async () => {
    if (confirmDeleteId === null) return
    const id = confirmDeleteId
    await deletePromotion.mutateAsync(id)
    addToast({ type: 'success', message: t('promotions.form.delete_success') })
    if (expandedId === id) setExpandedId(null)
    setConfirmDeleteId(null)
  }

  const statusVariants: Record<string, 'success' | 'warning' | 'ghost'> = {
    active: 'success',
    draft: 'ghost',
    expired: 'ghost',
    paused: 'warning',
  }

  const columns: Column<Promotion>[] = [
    {
      key: 'kind',
      header: t('promotions.fields.kind'),
      render: (p) => <span className="font-medium">{p.kind}</span>,
    },
    {
      key: 'value',
      header: t('promotions.fields.value'),
      render: (p) =>
        p.value_type === 'percent'
          ? `${p.value}%`
          : formatBaht(p.value),
    },
    {
      key: 'status',
      header: tc('labels.status'),
      render: (p) => (
        <Badge variant={statusVariants[p.status] ?? 'ghost'}>{t(`promotions.status.${p.status}`)}</Badge>
      ),
    },
    {
      key: 'redemptions_count',
      header: t('promotions.fields.redemptions_count'),
      render: (p) => p.redemptions_count,
      hideBelow: 'md',
    },
    {
      key: 'funding',
      header: t('promotions.fields.funding'),
      render: (p) => p.funding,
      hideBelow: 'lg',
    },
    {
      key: 'actions',
      header: '',
      render: (p) => (
        <div className="flex gap-1 justify-end">
          <Button
            size="xs"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation()
              setExpandedId(expandedId === p.id ? null : p.id)
            }}
            aria-label={expandedId === p.id ? tc('actions.hide') : tc('actions.view')}
          >
            {expandedId === p.id ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </Button>
          <Button
            size="xs"
            variant="ghost"
            className="text-error"
            onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(p.id) }}
            aria-label={tc('actions.delete')}
          >
            <Trash2 className="w-3 h-3" />
          </Button>
        </div>
      ),
    },
  ]

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('promotions.title') }]} />

      <PageHeader
        title={t('promotions.title')}
        description={t('promotions.subtitle')}
        isRefreshing={isFetching && !isLoading}
        primaryAction={{
          key: 'new-promotion',
          label: t('promotions.new_button'),
          icon: Plus,
          onClick: () => setFormOpen(true),
        }}
      />

      {formOpen && (
        <PromotionCreateForm
          onSuccess={() => { setFormOpen(false); refetch() }}
          onCancel={() => setFormOpen(false)}
        />
      )}

      {isLoading ? (
        <Card>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={tc('errors.something_went_wrong')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : !promotions.length ? (
        <Card>
          <EmptyState
            icon={<Ticket className="w-12 h-12" />}
            title={t('promotions.empty.title')}
            description={t('promotions.empty.description')}
          />
        </Card>
      ) : (
        <div className="space-y-2">
          <Card>
            <DataTable
              columns={columns}
              data={promotions}
              keyExtractor={(p) => p.id}
              onRowClick={(p) => setExpandedId(expandedId === p.id ? null : p.id)}
            />
          </Card>

          {expandedId !== null && (
            <Card>
              <RedemptionsDetail promotionId={expandedId} />
            </Card>
          )}
        </div>
      )}

      {data?.meta && data.meta.total_count > 0 && (
        <div className="flex justify-center">
          <Pagination
            currentPage={data.meta.current_page}
            totalPages={data.meta.total_pages}
            totalItems={data.meta.total_count}
            pageSize={data.meta.per_page}
            onPageChange={setPage}
          />
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDeleteId !== null}
        onClose={() => setConfirmDeleteId(null)}
        onConfirm={handleDelete}
        title={tc('actions.delete')}
        message={t('promotions.form.delete_confirm')}
        variant="error"
        loading={deletePromotion.isPending}
      />
    </div>
  )
}
